import { IconButton, LuIcon, Menu, stopPropagation } from "@overmap-ai/blocks"
import { FileCard as OvermapFileCard, type FileCardProps as OvermapFileCardProps } from "@overmap-ai/forms"
import type { NodeViewProps } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import { memo, useCallback } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { useEditorEditable } from "../../hooks"

export interface FileNodeComponentProps extends Pick<OvermapFileCardProps, "file" | "error">, NodeViewProps {}

export const FileNodeComponent = memo((props: FileNodeComponentProps) => {
	const {
		file,
		error,
		// ReactNodeViewProps
		editor,
		selected,
		deleteNode,
	} = props

	const isEditable = useEditorEditable(editor)

	const handleView = useCallback(() => {
		if (!file) return

		const objectUrl = URL.createObjectURL(file)
		window.open(objectUrl, "_blank")
		URL.revokeObjectURL(objectUrl)
	}, [file])

	const handleDownload = useCallback(() => {
		if (!file) return

		saveAs(file, file.name)
	}, [file])

	const handleDelete = useCallback(() => {
		deleteNode()
		editor.chain().focus().run()
	}, [deleteNode, editor])

	return (
		<OvermapFileCard
			className={cx("w-full", {
				pointer: file,
				"outline-(--primary-indicator) outline-2 -outline-offset-1":
					selected && editor.isEditable && editor.isFocused,
			})}
			size="sm"
			file={file}
			error={error}
			rightSlot={
				<div className="flex grow justify-end">
					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton
								variant="ghost"
								size="sm"
								accentColor="base"
								type="button"
								onClick={stopPropagation}
							>
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleView} disabled={!file}>
								<LuIcon icon={modelIconMapping.expand} />
								View
							</Menu.Item>

							<Menu.Item onSelect={handleDownload} disabled={!file}>
								<LuIcon icon={modelIconMapping.download} />
								Download
							</Menu.Item>

							{isEditable && (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={handleDelete} disabled={!file}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							)}
						</Menu.Content>
					</Menu.Root>
				</div>
			}
		/>
	)
})
FileNodeComponent.displayName = "FileNodeComponent"
